import { BonusType } from '@/webgl/runner/entities/BonusEntity'
import { CollectibleType } from '@/webgl/runner/entities/CollectibleEntity'
import { CollectibleResourceType } from '@/webgl/runner/rendering/CollectibleRenderer'
import { SequenceViewVariant } from '@/webgl/runner/scenarios/SequenceConfig.types'

import { ScenarioSetup } from '../ScenarioSetup.types'

const { Standard, Obstacle, TurningPoint, Bonus } = CollectibleType
const { Text, Pack } = CollectibleResourceType

const config: ScenarioSetup = {
  backgroundColor: 0xf1e2bd,
  lanesColor: 0x06748d,
  lanesExtraColor: 0x002938,
  lanesTextureId: 'rule',

  clearColor: 0xfff4e5,
  smokeColor: 0xf7ead8,

  playerColor: 0xf1e2bd,
  playerGoodColor: 0xfce5ab,
  playerWrongColor: 0xfe8769,

  audioSfx: { path: 'sfx_audio/sfx.json' },
  audio: {
    en: {
      path: 'francesca_1/audios/en/bundle_1.json',
      subtitles: 'francesca_1/audios/en/bundle_1.vtt',
    },
  },

  speed: {
    min: 2,
    max: 3.9,
  },

  collectibles: {
    resources: [
      {
        type: Pack,
        request: 'francesca_1/collectible.glb',
      },
      {
        type: Text,
        text: {
          font: 'fonts/UnderstoodSans-Heavy',
          style: {
            center: true,
            color: 0x06748d,
            outlineColor: 0xfff4e5,
            outline: 0.7,
            fontSize: 0.7,
            lineHeight: 1.25,
            alignMiddle: true,
          },
          values: [
            // { id: "subtract", content: "-" },
            // { id: "divide", content: "÷" },
            // { id: "add", content: "+" },
            // { id: "multiply", content: "x" },
            // { id: "subtract_switch", content: "÷" },
            // { id: "divide_switch", content: "-" },
            // { id: "add_switch", content: "x" },
            // { id: "multiply_switch", content: "+" },
            { id: 'R1', content: 'I' },
            { id: 'R2', content: 'II' },
            { id: 'R3', content: 'III' },
            { id: 'R4', content: 'IV' },
            { id: 'R5', content: 'V' },
            { id: 'R6', content: 'VI' },
            { id: 'R7', content: 'VII' },
            // { id: "R8", content: "VIII" },
            // { id: "R9", content: "IX" },
            // { id: "R10", content: "X" },
          ],
        },
      },
    ],

    items: [
      { id: 'R1', canSwitch: true, type: Standard },
      { id: 'R2', canSwitch: true, type: Standard },
      { id: 'R3', canSwitch: true, type: Standard },
      { id: 'R4', canSwitch: true, type: Standard },
      { id: 'R5', canSwitch: true, type: Standard },
      { id: 'R6', canSwitch: true, type: Standard },
      { id: 'R7', canSwitch: true, type: Standard },
      // { id: "R8", switchable: true, type: Standard },
      // { id: "R9", switchable: true, type: Standard },
      // { id: "R10", switchable: true, type: Standard },
      { id: 'subtract', canSwitch: true, type: Standard },
      { id: 'divide', canSwitch: true, type: Standard },
      { id: 'add', canSwitch: true, type: Standard },
      { id: 'multiply', canSwitch: true, type: Standard },
      { id: 'R1_shape', type: Standard },
      { id: 'R2_shape', type: Standard },
      { id: 'R3_shape', type: Standard },
      { id: 'R4_shape', type: Standard },
      { id: 'R5_shape', type: Standard },
      { id: 'R6_shape', type: Standard },
      // { id: "R7_shape" },
      // { id: "R8_shape" },
      // { id: "R9_shape" },
      // { id: "R10_shape" },
      {
        id: 'spider_lane',
        type: Obstacle,
        audio: 'TYCE_francesca_Scratch_eww get away 2',
      },
      {
        id: 'spider_top',
        type: Obstacle,
        audio: 'TYCE_francesca_Scratch_eww get away 2',
      },
      {
        id: 'switch',
        type: TurningPoint,
        bonus: BonusType.Switch,
      },
      {
        id: 'calculator',
        type: Bonus,
        bonus: BonusType.Focus,
        audio: 'TYCE_francesca_Scratch_yes finally 2',
      },
      {
        id: 'visual_chart',
        type: Bonus,
        bonus: BonusType.Chart,
      },
      {
        id: 'invisible_helmet',
        type: Bonus,
        bonus: BonusType.Helmet,
      },
    ],
  },

  particles: {
    resources: [{ id: 'default', request: 'common/particles.glb' }],
  },

  background: {
    resources: [
      {
        id: 'background',
        request: 'francesca_1/background.glb',
      },
    ],

    items: [
      { id: 'cut_apple' },
      { id: 'calculator' },
      { id: 'feuilles' },
      // { id: "papers", side: ScrollSide.RIGHT },
      { id: 'pen_01' },
      { id: 'pen_02' },
      { id: 'pen_pot' },
      { id: 'pencil_case' },
      { id: 'sharpener' },
      { id: 'spider_01' },
      { id: 'spider_02' },
      { id: 'spider_calculator' },
      { id: 'spider_cut_apple' },
      // { id: "spider_papers", side: ScrollSide.RIGHT },
      { id: 'spider_pen_01' },
      { id: 'spider_pen_pot' },
      { id: 'spider_pencil_case' },
      { id: 'spider_sharpener' },
    ],
  },

  sequences: [
    //#region equation_1_a
    {
      id: 'equation_1_a',

      goals: [['R3']],

      view: {
        variant: SequenceViewVariant.Equation,
        template: '{{R1}} + {{goal}} = {{R4}}',
      },

      stop: {
        alterJauge: 0.15,
      },

      track: {
        type: 'procedural',
        collectibles: ['R1', 'R2', 'R3', 'R5'],
      },

      scrolling: [
        'cut_apple',
        'calculator',
        // "papers",
        'pen_01',
        'pen_pot',
        'pencil_case',
        'sharpener',
      ],
    },
    //#endregion

    //#region equation_1_b
    {
      id: 'equation_1_b',

      goals: [['R4']],

      view: {
        variant: SequenceViewVariant.Equation,
        template: '{{R1}} + {{R2}} + {{goal}} = {{R7}}',
      },

      track: {
        type: 'procedural',
        collectibles: ['R1', 'R2', 'R3', 'R4', 'R5', 'R6'],
      },

      scrolling: [
        'cut_apple',
        'calculator',
        // "papers",
        'pen_01',
        'pen_pot',
        'pencil_case',
        'sharpener',
      ],
    },
    //#endregion

    //#region equation_1_visual
    {
      id: 'equation_1_visual',

      goals: [['R4']],

      view: {
        variant: SequenceViewVariant.Equation,
        template: '{{R2}} + {{goal}} = {{R6}}',
      },

      track: {
        type: 'procedural',
        collectibles: ['R1', 'R2', 'R3', 'R4', 'R5', 'R6'],
        barriers: ['visual_chart'],
      },

      scrolling: [
        'cut_apple',
        'calculator',
        // "papers",
        'pen_01',
        'pen_pot',
        'pencil_case',
        'sharpener',
      ],
    },
    //#endregion

    //#region equation_2_a
    {
      id: 'equation_2_a',

      goals: [['add']],

      view: {
        variant: SequenceViewVariant.Equation,
        template: '{{R1}} {{goal}} {{R2}} = {{R3}}',
      },

      track: {
        type: 'procedural',
        collectibles: ['subtract', 'divide', 'add', 'multiply'],
      },

      scrolling: [
        'cut_apple',
        'calculator',
        // "papers",
        'pen_01',
        'pen_02',
        'pen_pot',
        'pencil_case',
        'sharpener',
      ],
    },
    //#endregion

    //#region equation_2_switch
    {
      id: 'equation_2_switch_a',

      goals: [['add']],

      stop: {
        preserveBonus: true,
        preserveCollectibles: true,
      },

      view: {
        variant: SequenceViewVariant.Equation,
        template: '{{R7}} {{goal}} {{R3}} = {{R10}}',
      },

      start: {
        audio: 'TYCE_francesca_Scratch_oh wow 1',
      },

      track: {
        type: 'procedural',
        collectibles: ['subtract', 'divide', 'add', 'multiply'],
        barriers: ['switch'],
      },

      scrolling: [
        'cut_apple',
        'calculator',
        // "papers",
        'pen_01',
        'pen_02',
        'pen_pot',
        'pencil_case',
        'sharpener',
      ],
    },

    {
      id: 'equation_2_switch_b',

      goals: [['add']],

      view: {
        variant: SequenceViewVariant.Equation,
        template: '{{R7}} {{goal}} {{R3}} = {{R10}}',
      },

      start: {
        audio: 'TYCE_francesca_Scratch_wait i thought 2',
      },

      track: {
        type: 'procedural',
        collectibles: ['subtract', 'divide', 'add', 'multiply'],
      },

      scrolling: [
        'cut_apple',
        'calculator',
        // "papers",
        'pen_01',
        'pen_02',
        'pen_pot',
        'pencil_case',
        'sharpener',
      ],
    },

    //#endregion

    //#region equation_3_helmet_a
    {
      id: 'equation_3_helmet_a',

      goals: [['invisible_helmet']],

      stop: {
        preserveCollectibles: true,
        preserveBonus: true,
      },

      view: {
        variant: SequenceViewVariant.Equation,
        template: '{{R5}} + {{R3}} - {{goal}} = {{R6}}',
      },

      start: {
        audio: 'TYCE_francesca_Scratch_no not this 1',
      },

      track: {
        type: 'procedural',
        // mandatoryBonus: BonusType.Helmet,
        warmupTime: 3.0,
        collectibles: ['R1', 'R2', 'R3', 'R4', 'R5', 'invisible_helmet'],
        barriers: ['invisible_helmet'],
      },

      scrolling: [
        'cut_apple',
        'calculator',
        // "papers",
        'pen_01',
        'pen_02',
        'pen_pot',
        'pencil_case',
        'sharpener',
      ],
    },
    //#endregion

    //#region equation_3_helmet_b
    {
      id: 'equation_3_helmet_b',

      goals: [['R2']],

      start: {
        alterJauge: 0.8,
        audio: 'TYCE_francesca_Scratch_why whats going 2',
      },

      view: {
        variant: SequenceViewVariant.Equation,
        template: '{{R5}} + {{R3}} - {{goal}} = {{R6}}',
      },

      track: {
        type: 'procedural',
        collectibles: ['R1', 'R2', 'R3', 'R4', 'R5'],
        barriers: ['calculator'],
      },

      scrolling: [
        'cut_apple',
        'calculator',
        // "papers",
        'pen_01',
        'pen_02',
        'pen_pot',
        'pencil_case',
        'sharpener',
      ],
    },
    //#endregion

    //#region equation_3_spider_1
    {
      id: 'equation_3_spider_1',

      goals: [['R4']],

      condition: { id: 'spawned-count', count: 11 },

      stop: {
        preserveCollectibles: true,
        preserveBonus: true,
      },

      view: {
        variant: SequenceViewVariant.Equation,
        template: '( {{R7}} x {{goal}} ) {{goal}} {{R4}} = {{R3}}',
      },

      track: {
        type: 'procedural',
        collectibles: ['R1', 'R2', 'R3', 'R5', 'multiply', 'divide', 'add', 'subtract', 'spider_lane', 'spider_top'],
      },
      scrolling: [
        'cut_apple',
        'calculator',
        // "papers",
        'pen_01',
        'pen_02',
        'pen_pot',
        'pencil_case',
        'sharpener',
      ],
    },
    //#endregion

    //#region equation_3_spider_2
    {
      id: 'equation_3_spider_2',

      goals: [['R4']],

      condition: { id: 'spawned-count', count: 6 },

      stop: {
        preserveCollectibles: true,
        preserveBonus: true,
      },

      view: {
        variant: SequenceViewVariant.Equation,
        template: '( {{R7}} x {{goal}} ) {{goal}} {{R4}} = {{R3}}',
      },

      track: {
        type: 'procedural',
        collectibles: ['spider_lane', 'spider_top'],
      },

      scrolling: [
        'cut_apple',
        'calculator',
        // "papers",
        'pen_01',
        'pen_02',
        'spider_01',
        'spider_02',
        'pen_pot',
        'pencil_case',
        'sharpener',
      ],
    },
    //#endregion
  ],

  turningPoints: [],

  audioSegments: [
    {
      id: 'audio_intro',
      audios: ['TYCE_francesca_Scratch_Hi Im Francesca', 'TYCE_francesca_Scratch_math class 1'],
    },
    {
      id: 'audio_teacher',
      audios: ['TYCE_francesca_Scratch_hello-everyone-1_B'],
    },
    {
      id: 'audio_spider',
      track: {
        type: 'procedural',
        collectibles: ['spider_lane', 'spider_top'],
      },
      audios: [
        'TYCE_francesca_Scratch_my brain 1',
        'TYCE_francesca_Scratch_im not processing',
        'TYCE_francesca_Scratch_francesca-calm-1_B',
        'TYCE_francesca_Scratch_this isnt real 1',
      ],
    },
  ],
}

export default config
